/**
 * TanStack Query Hooks for Linked Table Operations
 *
 * Provides query hooks for fetching linked tables with:
 * - Automatic caching and deduplication
 * - Background refetching
 * - Error handling and retry logic
 */

import { useMemo } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import type { LinkedTableStatus, LinkedTableSourceApi } from '@loader/shared-types';
import type { VersionOption } from '@loader/ui';

import { useRpc, extractErrorMessage } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';
import type { LinkedTable } from '@/lib/types';

/**
 * Hook to fetch list of linked tables
 *
 * Features:
 * - Automatic caching (30s stale time)
 * - Optional filtering by status
 * - Pagination support (default max 500)
 */
export function useLinkedTablesListQuery(options?: {
  limit?: number;
  status?: 'active' | 'draft' | 'disabled';
  enabled?: boolean;
}) {
  const rpc = useRpc();

  return useQuery({
    queryKey: queryKeys.linkedTables.list({ limit: options?.limit, status: options?.status }),
    queryFn: async () => {
      const query: Record<string, string> = {
        limit: String(options?.limit ?? 500), // Default to max allowed by API
        offset: '0',
      };
      if (options?.status) query.status = options.status;

      const res = await rpc.api.private['linked-tables'].$get({ query });

      if (!res.ok) {
        throw new Error('Failed to fetch linked tables');
      }

      const response = await res.json();
      return response.data as unknown as LinkedTable[];
    },
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch all linked tables in the workspace
 *
 * Convenience alias for useLinkedTablesListQuery without filters.
 * Matches the naming convention of useFoldersQuery.
 *
 * Features:
 * - Automatic caching (30s stale time)
 * - Background refetching
 * - Fetches up to 500 linked tables (API max)
 */
export function useLinkedTablesQuery(options?: { enabled?: boolean }) {
  return useLinkedTablesListQuery(options);
}

/**
 * Hook to fetch a single linked table by ID
 *
 * Features:
 * - Automatic caching (30s stale time)
 * - Automatic refetching on mount if stale
 * - Error handling with retry logic
 */
export function useLinkedTableQuery(id: string | undefined) {
  const rpc = useRpc();

  return useQuery({
    queryKey: id ? queryKeys.linkedTables.detail(id) : ['linkedTables', 'detail', 'disabled'],
    queryFn: async () => {
      if (!id) throw new Error('Linked table ID is required');
      // @ts-expect-error - Hono RPC has limitations with hyphenated routes
      const res = await rpc.api.private['linked-tables'][':id'].$get({
        param: { id },
      });

      if (!res.ok) {
        throw new Error('Failed to fetch linked table');
      }

      const data = await res.json();
      return data.linkedTable as LinkedTable;
    },
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds - linked table metadata doesn't change often
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ============================================================================
// SOURCE TABLE VERSIONS (for ApiLinksTab)
// ============================================================================

/**
 * Result type for source table versions query
 */
export interface SourceTableVersionsResult {
  tableId: string;
  tableName: string;
  versions: VersionOption[];
}

/**
 * Hook to fetch versions for all source tables of a linked table
 *
 * Features:
 * - Parallel fetching of table names and versions using useQueries
 * - Automatic caching with table/version query keys
 * - Only fetches for sources of type 'table' (not linked_table)
 *
 * @param sources - Array of linked table sources
 * @param options - Query options (enabled)
 * @returns Object with sourceTableVersions map, loading state, and initial selections
 */
export function useLinkedTableSourceVersionsQuery(
  sources: LinkedTableSourceApi[],
  options?: { enabled?: boolean }
) {
  const rpc = useRpc();

  // Filter to table sources only
  const tableSources = useMemo(
    () => sources.filter((s) => s.type === 'table'),
    [sources]
  );

  // Fetch table details (for name) and versions in parallel for each source
  const queries = useQueries({
    queries: tableSources.flatMap((source) => [
      // Query for table name
      {
        queryKey: queryKeys.tables.detail(source.target_id),
        queryFn: async () => {
          // @ts-expect-error - Hono RPC type inference limitation
          const res = await rpc.api.private.tables[':id'].$get({
            param: { id: source.target_id },
          });
          if (!res.ok) throw new Error('Failed to fetch table');
          const data = await res.json();
          return { alias: source.alias, tableName: data.table?.name || source.alias };
        },
        enabled: options?.enabled !== false,
        staleTime: 30 * 1000,
      },
      // Query for versions
      {
        queryKey: queryKeys.tables.versions(source.target_id),
        queryFn: async () => {
          const res = await rpc.api.private.versions.$get({
            query: { table_id: source.target_id },
          });
          if (!res.ok) throw new Error('Failed to fetch versions');
          const response = await res.json();
          const versionsArray = response.data || [];
          return {
            alias: source.alias,
            tableId: source.target_id,
            versions: (versionsArray as VersionOption[]).filter((v) => v.status === 'completed'),
          };
        },
        enabled: options?.enabled !== false,
        staleTime: 10 * 1000,
      },
    ]),
  });

  // Combine results into the expected format
  const result = useMemo(() => {
    const sourceTableVersions: Record<string, SourceTableVersionsResult> = {};
    const initialSelections: Record<string, string> = {};

    // Process pairs of results (table name + versions)
    for (let i = 0; i < tableSources.length; i++) {
      const source = tableSources[i];
      const tableQuery = queries[i * 2];
      const versionsQuery = queries[i * 2 + 1];

      if (tableQuery?.data && versionsQuery?.data) {
        const tableData = tableQuery.data as { alias: string; tableName: string };
        const versionsData = versionsQuery.data as { alias: string; tableId: string; versions: VersionOption[] };

        sourceTableVersions[source.alias] = {
          tableId: versionsData.tableId,
          tableName: tableData.tableName,
          versions: versionsData.versions,
        };
        initialSelections[source.alias] = 'current';
      }
    }

    return { sourceTableVersions, initialSelections };
  }, [tableSources, queries]);

  const isLoading = queries.some((q) => q.isLoading);

  return {
    sourceTableVersions: result.sourceTableVersions,
    initialSelections: result.initialSelections,
    isLoading,
  };
}

// ============================================================================
// MUTATIONS
// ============================================================================

/**
 * Hook to update linked table status
 *
 * Features:
 * - Automatic cache invalidation
 * - Error handling
 * - Toast notifications
 */
export function useUpdateLinkedTableStatusMutation() {
  const rpc = useRpc();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: LinkedTableStatus }) => {
      // @ts-expect-error - Hono RPC has limitations with hyphenated routes
      const res = await rpc.api.private['linked-tables'][':id'].$put({
        param: { id },
        json: { status },
      });

      if (!res.ok) {
        throw new Error(await extractErrorMessage(res, 'Failed to update linked table status'));
      }

      return res.json();
    },
    onSuccess: (_, variables) => {
      // Invalidate related queries
      void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.linkedTables.detail(variables.id) });
      void queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'dataGrid' &&
          query.queryKey[1] === 'linked-tables',
      });
    },
  });
}
